import { trackPublicCtaViewed } from '~publish/helpers/publicTracking'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import { Dialog } from '@buffer-mono/popcorn'

import type { GuestMedia } from '~publish/helpers/guestMedia'
import { GUEST_NETWORKS } from '~publish/helpers/guestNetworks'
import type { GuestNetworkId } from '~publish/helpers/guestNetworks'
import type { GuestMediaAccessController } from '~publish/hooks/useGuestMediaAccess'

import { getPublicShareDestination } from './publicShareDestinations'
import { PublicShareNowFork } from './PublicShareNowFork'
import { PublicShareNowHandoff } from './PublicShareNowHandoff'
import type { PublicShareNowTarget } from './PublicShareNowHandoff'

import styles from './PublicShareNowDialog.module.css'

type ShareStep = 'fork' | 'handoff'

export function PublicShareNowDialog({
  open,
  text,
  media,
  mediaAccess,
  networks,
  onOpenChange,
  onCreateAccount,
}: {
  open: boolean
  text: string
  media: readonly GuestMedia[]
  mediaAccess: GuestMediaAccessController
  networks: readonly GuestNetworkId[]
  onOpenChange: (open: boolean) => void
  onCreateAccount: () => boolean
}): JSX.Element {
  const [step, setStep] = useState<ShareStep>('fork')
  const [hasAccountHandoffError, setHasAccountHandoffError] = useState(false)
  const titleRef = useRef<HTMLHeadingElement>(null)
  const targets: readonly PublicShareNowTarget[] = GUEST_NETWORKS.filter(
    (network) => networks.includes(network.id),
  ).flatMap((network) => {
    const destination = getPublicShareDestination(network.id)
    return destination ? [{ network, destination }] : []
  })

  const handleOpenChange = useCallback(
    (nextOpen: boolean): void => {
      if (!nextOpen) {
        setStep('fork')
        setHasAccountHandoffError(false)
      }
      onOpenChange(nextOpen)
    },
    [onOpenChange],
  )

  useEffect(() => {
    if (open) titleRef.current?.focus()
  }, [open, step])

  useEffect(() => {
    if (open && step === 'fork')
      trackPublicCtaViewed('publish-composer-shareNowPanel-signupPrompt-1')
  }, [open, step])

  const createAccount = (): void => {
    setHasAccountHandoffError(false)
    if (!onCreateAccount()) setHasAccountHandoffError(true)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content
        aria-describedby={undefined}
        className={styles.content}
        data-step={step}
        onOpenAutoFocus={(event): void => {
          event.preventDefault()
          titleRef.current?.focus()
        }}
      >
        {step === 'fork' ? (
          <PublicShareNowFork
            titleRef={titleRef}
            hasAccountHandoffError={hasAccountHandoffError}
            onCreateAccount={createAccount}
            onShareManually={(): void => setStep('handoff')}
          />
        ) : (
          <PublicShareNowHandoff
            titleRef={titleRef}
            text={text}
            media={media}
            mediaAccess={mediaAccess}
            targets={targets}
            onBack={(): void => setStep('fork')}
          />
        )}
        <Dialog.CloseButton className={styles.close} />
      </Dialog.Content>
    </Dialog>
  )
}
